import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {IonPopover} from '@ionic/angular';
import {Operatore} from '../../../_models/Operatore';
import {Subscription} from 'rxjs';
import {AuthenticationService} from '../../../_services/authentication.service';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {Router} from '@angular/router';
import {blurActiveOverlayFocus, PopoverBackdropGuard, getPopoverHostElement} from '../../../_helpers/popover-focus-guard';

@Component({
  selector: 'app-user-info',
  templateUrl: './user-info.component.html',
  styleUrls: ['./user-info.component.scss'],
  standalone: false
})
export class UserInfoComponent implements OnInit, OnDestroy {
  @ViewChild('popover') popover?: IonPopover;
  @ViewChild('triggerButton', {read: ElementRef}) triggerButton?: ElementRef<HTMLElement>;
  isOpen = false;
  firstName = '';
  lastName = '';
  email = '';
  imageUrl: string | null = null;
  organizzazioneCode = '';
  organizzazioneName = '';
  subscription: Subscription;
  private readonly backdropGuard = new PopoverBackdropGuard(
    () => getPopoverHostElement(this.popover),
    () => this.releaseFocus()
  );
  private readonly storageBaseUrl = 'https://archicloudstore.blob.core.windows.net/archiconnect-container';

  constructor(
    private router: Router,
    private organizzazioniService: OrganizzazioniService,
    public authService: AuthenticationService) { }

  ngOnInit() {
    this.subscription = this.authService.currentUser.subscribe((_user: Operatore) => {
      if (_user != null) {
        this.firstName = _user.firstName?.trim() ?? '';
        this.lastName = _user.lastName?.trim() ?? '';
        this.email = _user.email?.trim() ?? '';
        this.imageUrl = this.buildImageUrl(_user.imageUrl);
      } else {
        this.firstName = '';
        this.lastName = '';
        this.email = '';
        this.imageUrl = null;
      }
    });
    this.organizzazioneCode = this.organizzazioniService.organizzazioneCodice;
    this.subscription.add(this.organizzazioniService.currentOrganizzazione
      .subscribe(() => {
        if (this.organizzazioniService.currentOrganizzazioneValue !== null) {
          this.organizzazioneName = this.organizzazioniService.currentOrganizzazioneValue.descrizione;
        }
        this.organizzazioneCode = this.organizzazioniService.organizzazioneCodice;
      }));
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.backdropGuard.detach();
  }

  onImageError(): void {
    this.imageUrl = null;
  }

  onDidPresent = () => {
    this.backdropGuard.attach();
  };

  onWillClose = () => {
    this.backdropGuard.blurBackdrop();
    this.releaseFocus();
  };

  onClose = () => {
    this.isOpen = false;
    this.backdropGuard.blurBackdrop();
    this.releaseFocus();
  };

  presentPopover(event: Event) {
    this.releaseFocus(event);

    if (this.popover) {
      this.popover.event = event;
    }
    this.isOpen = true;
  }

  closePopover = async () => {
    this.releaseFocus();
    this.isOpen = false;

    if (this.popover) {
      await this.popover.dismiss().catch(() => undefined);
    }
  };

  navigateTo = async (url: string) => {
    await this.closePopover();
    await this.router.navigate([url]);
  };

  logout = async () => {
    this.releaseFocus();
    // Naviga PRIMA di distruggere la sessione: se la rotta corrente ha un CanDeactivate guard
    // (es. linea bilancia attiva su spedizione-manuale), l'utente è ancora autenticato quando
    // il guard esegue la sua logica, e annullare la navigazione annulla anche il logout.
    // onSameUrlNavigation: 'reload' forza la navigazione anche se si è già su '/' (dashboard):
    // senza, Angular ignora la navigazione verso l'URL corrente e risolve la Promise a `false`,
    // impedendo il logout quando il pulsante viene premuto dalla dashboard.
    await this.closePopover();
    const navigated = await this.router.navigate(['/'], { onSameUrlNavigation: 'reload' });
    // Con revoca del refresh token: logout() da solo lo lascerebbe valido per 7 giorni.
    if (navigated) void this.authService.logoutAndRevoke();
  };

  get initials(): string {
    return `${this.firstName.charAt(0)}${this.lastName.charAt(0)}`.trim().toUpperCase();
  }

  get isAuthenticated(): boolean {
    return !!this.authService.currentUserValue?.jwtToken;
  }

  get displayName(): string {
    return [this.firstName, this.lastName].filter(Boolean).join(' ').trim();
  }

  private buildImageUrl(imagePath?: string | null): string | null {
    const normalizedImagePath = imagePath?.trim();

    if (!normalizedImagePath || normalizedImagePath === 'null' || normalizedImagePath === 'undefined') {
      return null;
    }

    return `${this.storageBaseUrl}/${normalizedImagePath}`;
  }

  private releaseFocus(event?: Event): void {
    blurActiveOverlayFocus(event, this.triggerButton?.nativeElement);
  }
}
